import React, { useMemo } from 'react';
import { GasGeoMap, type GasGeoPoint } from '../../../shared/kit/geo';
import type { AgendaItem } from '../types';

type TFn = (key: string, vars?: Record<string, string | number | boolean | null | undefined>, fallback?: string) => string;

interface Props {
    items: AgendaItem[];
    t: TFn;
    mapStyleUrl?: string;
    mapStyleUrlDark?: string;
}

/**
 * 5e vue de l'agenda : carte des activités localisées (read-model `geo`).
 * Filtre client sur les items déjà filtrés par type/texte/lieu/mois.
 */
export function AgendaMapView({ items, t, mapStyleUrl, mapStyleUrlDark }: Props): React.ReactElement {
    const points = useMemo<GasGeoPoint<AgendaItem>[]>(() => {
        const out: GasGeoPoint<AgendaItem>[] = [];
        for (const it of items) {
            const geo = it.geo;
            if (!geo || typeof geo.lat !== 'number' || typeof geo.lng !== 'number') {
                continue;
            }
            if (!Number.isFinite(geo.lat) || !Number.isFinite(geo.lng)) {
                continue;
            }
            out.push({
                id: String(it.id),
                lat: geo.lat,
                lng: geo.lng,
                label: it.title,
                data: it,
            });
        }
        return out;
    }, [items]);

    const cluster = useMemo(() => ({ radius: 50, maxZoom: 14, minPoints: 3 }), []);

    if (points.length === 0) {
        return (
            <p className="gas-agenda__map-empty">
                {t('agenda.map.empty', {}, 'Aucune activité localisée à afficher sur la carte.')}
            </p>
        );
    }

    return (
        <div className="gas-agenda__map" role="region" aria-label={t('agenda.map.label', {}, 'Carte des activités')}>
            <p className="gas-agenda__map-count">
                {t('agenda.map.count', { n: points.length }, '%n% activités sur la carte')}
            </p>
            <div className="gas-agenda__map-canvas">
                <GasGeoMap<AgendaItem>
                    points={points}
                    styleUrl={mapStyleUrl}
                    darkStyleUrl={mapStyleUrlDark}
                    cluster={cluster}
                    refitOnPointsChange
                    renderPopup={(point: GasGeoPoint<AgendaItem>) => {
                        const item = point.data;
                        if (!item) {
                            return <p className="gas-agenda__map-popup-title">{point.label}</p>;
                        }
                        return (
                            <div className="gas-agenda__map-popup">
                                {item.image_url ? (
                                    <img
                                        className="gas-agenda__map-popup-image"
                                        src={item.image_url}
                                        alt=""
                                    />
                                ) : null}
                                <p className="gas-agenda__map-popup-title">{item.title}</p>
                                {item.date_short ? (
                                    <p className="gas-agenda__map-popup-meta">{item.date_short}</p>
                                ) : null}
                                {item.lieu ? (
                                    <p className="gas-agenda__map-popup-meta">{item.lieu}</p>
                                ) : null}
                                {item.url ? (
                                    <a className="gas-agenda__map-popup-link" href={item.url}>
                                        {t('common.details', {}, 'Détails')}
                                    </a>
                                ) : null}
                            </div>
                        );
                    }}
                />
            </div>
        </div>
    );
}
